import { DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  ViewChild,
  effect,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ContactMessageService } from '@app/services/content/contact-messages/contact-message.service';
import { InquiryDrawerService } from '@app/services/ui/inquiry-drawer/inquiry-drawer.service';
import { PublicToastService } from '@app/services/ui/public-toast/public-toast.service';
import { getValidationMessage, shouldShowValidationError } from '@app/validators/validation-messages';

const INQUIRY_DRAFT_KEY = 'hytec-inquiry-draft';
const PERSON_NAME_PATTERN = /^[\p{L}][\p{L}\p{M} .'-]*$/u;
const CONTACT_NUMBER_PATTERN = /^\+?[0-9][0-9\s()-]{6,19}$/;

@Component({
  selector: 'app-floating-nav',
  standalone: true,
  imports: [ReactiveFormsModule, RouterModule],
  templateUrl: './floating-nav.html',
  styleUrl: './floating-nav.scss',
})
export class FloatingNavComponent implements OnDestroy {
  @ViewChild('drawerCloseButton') drawerCloseButton?: ElementRef<HTMLButtonElement>;

  private readonly document = inject(DOCUMENT);
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly contactMessageService = inject(ContactMessageService);
  private readonly publicToastService = inject(PublicToastService);
  readonly inquiryDrawer = inject(InquiryDrawerService);

  readonly inquiryTypes = [
    'Product inquiry',
    'Request for quotation',
    'Training inquiry',
    'Partnership opportunity',
    'Technical support',
    'Schedule a visit',
    'Other',
  ];

  readonly inquiryForm = this.fb.group({
    name: [
      '',
      [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(50),
        Validators.pattern(PERSON_NAME_PATTERN),
      ],
    ],
    email: ['', [Validators.required, Validators.email, Validators.maxLength(50)]],
    phone: ['', [Validators.required, Validators.pattern(CONTACT_NUMBER_PATTERN)]],
    organization: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(150)]],
    inquiryType: ['', Validators.required],
    otherInquiryType: ['', Validators.maxLength(100)],
    message: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(1000)]],
    privacyConsent: [false, Validators.requiredTrue],
  });

  isSubmitting = false;
  hasSubmitted = false;
  private previousBodyOverflow = '';
  private previouslyFocusedElement: HTMLElement | null = null;

  constructor() {
    this.restoreDraft();
    this.updateOtherInquiryValidation(this.inquiryForm.controls.inquiryType.value);
    this.inquiryForm.controls.inquiryType.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((inquiryType) => this.updateOtherInquiryValidation(inquiryType));
    this.inquiryForm.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.saveDraft());

    effect(() => {
      if (this.inquiryDrawer.isOpen()) {
        this.lockPageScroll();
        this.previouslyFocusedElement = this.document.activeElement as HTMLElement | null;
        setTimeout(() => this.drawerCloseButton?.nativeElement.focus());
        return;
      }

      this.unlockPageScroll();
      this.previouslyFocusedElement?.focus();
      this.previouslyFocusedElement = null;
    });
  }

  ngOnDestroy(): void {
    this.unlockPageScroll();
  }

  @HostListener('document:keydown.escape')
  closeOnEscape(): void {
    if (this.inquiryDrawer.isOpen() && !this.isSubmitting) {
      this.closeDrawer();
    }
  }

  openDrawer(): void {
    this.hasSubmitted = false;
    this.inquiryDrawer.open();
  }

  closeDrawer(): void {
    if (!this.isSubmitting) {
      this.inquiryDrawer.close();
    }
  }

  submitInquiry(): void {
    this.hasSubmitted = true;

    if (this.isSubmitting || this.inquiryForm.invalid) {
      this.inquiryForm.markAllAsTouched();
      return;
    }

    const values = this.inquiryForm.getRawValue();
    const pageTitle = this.document.title || 'Hytec Power website';
    const inquiryType =
      values.inquiryType === 'Other' ? values.otherInquiryType?.trim() : values.inquiryType;

    this.isSubmitting = true;
    this.contactMessageService
      .sendMessage({
        name: values.name?.trim() || '',
        email: values.email?.trim() || '',
        companyName: values.organization?.trim() || '',
        companyPhone: values.phone?.trim() || '',
        subject: `${inquiryType}: ${pageTitle}`,
        message: [
          `Inquiry type: ${inquiryType}`,
          `Page: ${pageTitle}`,
          `Page URL: ${this.getPageUrl()}`,
          '',
          values.message?.trim() || '',
        ].join('\n'),
      })
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.publicToastService.show({
            title: 'Inquiry sent',
            message: 'Thank you. The Hytec team will contact you as soon as possible.',
            type: 'success',
          });
          this.clearForm();
          this.inquiryDrawer.close();
        },
        error: (error) => {
          this.isSubmitting = false;
          this.publicToastService.show({
            title: 'Inquiry not sent',
            message: error?.error?.message || 'Please try again or use one of our contact methods.',
            type: 'error',
          });
        },
      });
  }

  showFieldError(fieldName: keyof typeof this.inquiryForm.controls): boolean {
    const control = this.inquiryForm.controls[fieldName];

    return shouldShowValidationError(control, this.hasSubmitted);
  }

  fieldDescribedBy(fieldName: keyof typeof this.inquiryForm.controls): string | null {
    return this.showFieldError(fieldName) ? `inquiry-${fieldName}-error` : null;
  }

  fieldError(fieldName: keyof typeof this.inquiryForm.controls): string {
    const control = this.inquiryForm.controls[fieldName];
    if (control.errors?.['requiredTrue']) {
      return 'Please confirm that you agree to the Privacy Policy.';
    }
    if (control.errors?.['required']) {
      return fieldName === 'privacyConsent'
        ? 'Please confirm that you agree to the Privacy Policy.'
        : 'This field is required.';
    }
    if (control.errors?.['email']) {
      return 'Enter a valid email address.';
    }
    if (control.errors?.['pattern']) {
      return fieldName === 'name'
        ? 'Use letters, spaces, apostrophes, periods, or hyphens only.'
        : 'Use 7–20 digits and only +, spaces, parentheses, or hyphens.';
    }
    if (control.errors?.['minlength']) {
      return fieldName === 'message'
        ? 'Provide at least 10 characters so we can assist you.'
        : 'This value is too short.';
    }
    if (control.errors?.['maxlength']) {
      const limits: Partial<Record<keyof typeof this.inquiryForm.controls, number>> = {
        name: 50,
        email: 50,
        organization: 150,
        otherInquiryType: 100,
        message: 1000,
      };

      return `Use no more than ${limits[fieldName]} characters.`;
    }

    return getValidationMessage(control);
  }

  private getPageUrl(): string {
    const origin = this.document.defaultView?.location.origin || '';

    return `${origin}${this.router.url}`;
  }

  private saveDraft(): void {
    try {
      this.document.defaultView?.sessionStorage.setItem(
        INQUIRY_DRAFT_KEY,
        JSON.stringify(this.inquiryForm.getRawValue()),
      );
    } catch {
      // The form remains usable when browser storage is unavailable.
    }
  }

  private restoreDraft(): void {
    try {
      const storedDraft = this.document.defaultView?.sessionStorage.getItem(INQUIRY_DRAFT_KEY);
      if (storedDraft) {
        this.inquiryForm.patchValue(JSON.parse(storedDraft), { emitEvent: false });
      }
    } catch {
      this.document.defaultView?.sessionStorage.removeItem(INQUIRY_DRAFT_KEY);
    }
  }

  private clearForm(): void {
    this.hasSubmitted = false;
    this.inquiryForm.reset({
      name: '',
      email: '',
      phone: '',
      organization: '',
      inquiryType: '',
      otherInquiryType: '',
      message: '',
      privacyConsent: false,
    });
    this.document.defaultView?.sessionStorage.removeItem(INQUIRY_DRAFT_KEY);
  }

  private lockPageScroll(): void {
    this.previousBodyOverflow = this.document.body.style.overflow;
    this.document.body.style.overflow = 'hidden';
  }

  private unlockPageScroll(): void {
    this.document.body.style.overflow = this.previousBodyOverflow;
  }

  private updateOtherInquiryValidation(inquiryType: string | null): void {
    const otherInquiryType = this.inquiryForm.controls.otherInquiryType;

    if (inquiryType === 'Other') {
      otherInquiryType.setValidators([
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(100),
      ]);
    } else {
      otherInquiryType.setValidators(Validators.maxLength(100));
      otherInquiryType.setValue('', { emitEvent: false });
    }

    otherInquiryType.updateValueAndValidity({ emitEvent: false });
  }
}
